import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  InputBase,
  Avatar,
  Tooltip,
  Button,
  Chip,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl
} from '@mui/material';
import {
  Search,
  MoreVert,
  ViewModule,
  ChevronLeft,
  Close,
  Chat,
  Refresh,
  Bookmark,
  BugReport,
  Flag,
  Folder,
  Schedule,
  PersonOutlined,
  Assignment
} from '@mui/icons-material';
import { Project, JiraIssue, JiraSprint } from '../../types';
import {
  getActiveSprints,
  getSprintIssues,
  JiraApiError
} from '../../services/jiraService';
import { JiraIssueDetailsDialog } from '../jira/JiraIssueDetailsDialog';
import { useAuth } from '../../context/AuthContext';

interface ProjectsSidebarProps {
  projects: Project[];
  selectedProject: Project;
  onSelectProject: (project: Project) => void;
  onCollapse?: () => void;
  onClose?: () => void;
  onOpenInChat?: () => void;
  isMobile?: boolean;
}

const ProjectsSidebar: React.FC<ProjectsSidebarProps> = ({
  projects,
  selectedProject,
  onSelectProject,
  onCollapse,
  onClose,
  onOpenInChat,
  isMobile
}) => {
  const { user, firebaseUser } = useAuth();

  // Navigation tab: 'sprint' (Active Sprint) | 'my-work' (My Work filtered by email) | 'projects' (Projects list)
  const [activeTab, setActiveTab] = useState<'sprint' | 'my-work' | 'projects'>('sprint');

  // Search & filter state
  const [projectSearch, setProjectSearch] = useState('');
  const [storySearch, setStorySearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'IN PROGRESS' | 'DONE' | 'TO DO'>('ALL');

  // Active Sprint data (GET /jira/projects/{project_key}/active-sprint)
  const [activeSprint, setActiveSprint] = useState<JiraSprint | null>(null);
  // Sprint Issues data (GET /jira/sprints/{sprint_id}/issues)
  const [sprintIssues, setSprintIssues] = useState<JiraIssue[]>([]);
  const [isLoadingSprint, setIsLoadingSprint] = useState<boolean>(false);
  const [sprintError, setSprintError] = useState<string | null>(null);

  // Selected story for full details modal dialog (GET /jira/issues/{key} + /comments)
  const [selectedIssueKey, setSelectedIssueKey] = useState<string | null>(null);

  /**
   * Fetches active sprint and sprint issues for the selected project.
   * Calls:
   * 1. GET /jira/projects/{project_key}/active-sprint
   * 2. If sprint exists: GET /jira/sprints/{sprint_id}/issues
   */
  const loadSprintData = useCallback(async (projectKey: string) => {
    if (!projectKey) {
      setActiveSprint(null);
      setSprintIssues([]);
      return;
    }

    // Immediately clear previous data to never display stale issues
    setActiveSprint(null);
    setSprintIssues([]);
    setIsLoadingSprint(true);
    setSprintError(null);

    try {
      const sprints = await getActiveSprints(projectKey);
      const sprint = sprints.length > 0 ? sprints[0] : null;
      setActiveSprint(sprint);

      // Only call sprint-issues if active sprint returned
      const sprintId = sprint?.id ?? (sprint as any)?.sprint_id;
      if (sprintId) {
        const issues = await getSprintIssues(sprintId);
        setSprintIssues(Array.isArray(issues) ? issues : []);
      } else {
        setSprintIssues([]);
      }
    } catch (err: any) {
      console.warn(`Failed to fetch active sprint for ${projectKey}:`, err);
      const msg = err instanceof JiraApiError ? err.message : `No active sprint for ${projectKey}.`;
      setSprintError(msg);
      setActiveSprint(null);
      setSprintIssues([]);
    } finally {
      setIsLoadingSprint(false);
    }
  }, []);

  // When selected project changes, trigger active-sprint endpoint call
  useEffect(() => {
    if (selectedProject?.code) {
      loadSprintData(selectedProject.code);
    }
  }, [selectedProject?.code, loadSprintData]);

  // My Active Work: filtered directly from sprintIssues state based on user email/name (zero extra API calls)
  const myIssues = useMemo(() => {
    if (!sprintIssues.length) return [];
    const userEmailLower = (user?.email || firebaseUser?.email || '').toLowerCase().trim();
    const userNameLower = (user?.displayName || firebaseUser?.displayName || '').toLowerCase().trim();

    return sprintIssues.filter((issue) => {
      const assigneeEmail = (issue.assignee?.emailAddress || '').toLowerCase().trim();
      const assigneeName = (issue.assignee?.displayName || '').toLowerCase().trim();

      if (userEmailLower && assigneeEmail && assigneeEmail === userEmailLower) return true;
      if (userEmailLower && assigneeName && (userEmailLower.includes(assigneeName) || assigneeName.includes(userEmailLower.split('@')[0]))) return true;
      if (userNameLower && assigneeName && (assigneeName === userNameLower || assigneeName.includes(userNameLower) || userNameLower.includes(assigneeName))) return true;
      return false;
    });
  }, [sprintIssues, user, firebaseUser]);

  // Filtered projects
  const filteredProjects = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
      p.code.toLowerCase().includes(projectSearch.toLowerCase())
  );

  // Issues to display based on active tab
  const currentIssuesList = activeTab === 'sprint' ? sprintIssues : myIssues;

  // Filtered Jira issues
  const filteredIssues = useMemo(() => {
    return currentIssuesList.filter((issue) => {
      const query = storySearch.trim().toLowerCase();
      const matchesQuery =
        !query ||
        issue.key.toLowerCase().includes(query) ||
        issue.summary.toLowerCase().includes(query) ||
        (issue.assignee?.displayName && issue.assignee.displayName.toLowerCase().includes(query));

      const statusNormalized = (issue.status || '').toUpperCase();
      let matchesStatus = true;
      if (statusFilter === 'IN PROGRESS') {
        matchesStatus = statusNormalized.includes('PROGRESS') || statusNormalized.includes('REVIEW');
      } else if (statusFilter === 'DONE') {
        matchesStatus = statusNormalized.includes('DONE') || statusNormalized.includes('RESOLVED');
      } else if (statusFilter === 'TO DO') {
        matchesStatus = statusNormalized.includes('TODO') || statusNormalized.includes('TO DO') || statusNormalized.includes('BACKLOG');
      }

      return matchesQuery && matchesStatus;
    });
  }, [currentIssuesList, storySearch, statusFilter]);

  const getIssueTypeIcon = (type?: string) => {
    const t = (type || '').toLowerCase();
    if (t.includes('bug')) return <BugReport sx={{ fontSize: 15, color: '#FF6577' }} />;
    if (t.includes('task')) return <Assignment sx={{ fontSize: 15, color: '#4F6BFF' }} />;
    if (t.includes('epic')) return <Flag sx={{ fontSize: 15, color: '#9B51E0' }} />;
    return <Bookmark sx={{ fontSize: 15, color: '#22C99A' }} />;
  };

  const getStatusColor = (status?: string) => {
    const s = (status || '').toUpperCase();
    if (s.includes('DONE') || s.includes('RESOLVED')) {
      return { bgcolor: 'rgba(34, 201, 154, 0.14)', color: '#1ca87f', border: '1px solid rgba(34, 201, 154, 0.3)' };
    }
    if (s.includes('PROGRESS') || s.includes('REVIEW')) {
      return { bgcolor: 'rgba(79, 107, 255, 0.14)', color: 'var(--hp-primary)', border: '1px solid rgba(79, 107, 255, 0.3)' };
    }
    return { bgcolor: 'var(--hp-surface-raised)', color: 'var(--hp-text-secondary)', border: '1px solid var(--hp-border)' };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1.5, width: '100%', minWidth: 0 }}>
      {/* Top Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.05rem', color: 'var(--hp-text)' }}>
            Projects & Jira
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'var(--hp-primary)',
              bgcolor: 'var(--hp-primary-light)',
              px: 1,
              py: 0.2,
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.72rem'
            }}
          >
            {activeTab === 'sprint'
              ? `${sprintIssues.length} Sprint Issues`
              : activeTab === 'my-work'
              ? `${myIssues.length} Assigned`
              : `${projects.length} Projects`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Refresh Jira Button */}
          <Tooltip title="Refresh active sprint for selected project">
            <IconButton
              size="small"
              onClick={() => selectedProject?.code && loadSprintData(selectedProject.code)}
              disabled={isLoadingSprint}
              sx={{
                bgcolor: 'var(--hp-surface-raised)',
                border: '1px solid var(--hp-border)',
                p: 0.6
              }}
            >
              {isLoadingSprint ? <CircularProgress size={16} /> : <Refresh sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>

          {/* Close / Collapse button */}
          {onClose ? (
            <Tooltip title="Close drawer">
              <IconButton size="small" onClick={onClose} sx={{ color: 'var(--hp-text-secondary)' }}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : onCollapse ? (
            <Tooltip title="Collapse sidebar">
              <IconButton size="small" onClick={onCollapse} aria-label="Collapse sidebar" sx={{ color: 'var(--hp-text-secondary)' }}>
                <ChevronLeft fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      {/* Segmented Navigation Tab Switcher: Sprint, My Work, Projects */}
      <Box
        sx={{
          display: 'flex',
          p: 0.4,
          bgcolor: 'var(--hp-surface)',
          borderRadius: '10px',
          border: '1px solid var(--hp-border)',
          gap: 0.3,
          flexShrink: 0
        }}
      >
        <Button
          size="small"
          startIcon={<Schedule sx={{ fontSize: 14 }} />}
          onClick={() => setActiveTab('sprint')}
          sx={{
            flex: 1,
            py: 0.4,
            px: 0.8,
            borderRadius: '7px',
            textTransform: 'none',
            fontSize: '0.74rem',
            fontWeight: activeTab === 'sprint' ? 700 : 500,
            color: activeTab === 'sprint' ? 'var(--hp-primary)' : 'var(--hp-text-secondary)',
            bgcolor: activeTab === 'sprint' ? 'var(--hp-surface-raised)' : 'transparent',
            whiteSpace: 'nowrap'
          }}
        >
          Sprint ({sprintIssues.length})
        </Button>

        <Button
          size="small"
          startIcon={<PersonOutlined sx={{ fontSize: 14 }} />}
          onClick={() => setActiveTab('my-work')}
          sx={{
            flex: 1,
            py: 0.4,
            px: 0.8,
            borderRadius: '7px',
            textTransform: 'none',
            fontSize: '0.74rem',
            fontWeight: activeTab === 'my-work' ? 700 : 500,
            color: activeTab === 'my-work' ? 'var(--hp-primary)' : 'var(--hp-text-secondary)',
            bgcolor: activeTab === 'my-work' ? 'var(--hp-surface-raised)' : 'transparent',
            whiteSpace: 'nowrap'
          }}
        >
          My Work ({myIssues.length})
        </Button>

        <Button
          size="small"
          startIcon={<Folder sx={{ fontSize: 14 }} />}
          onClick={() => setActiveTab('projects')}
          sx={{
            flex: 1,
            py: 0.4,
            px: 0.8,
            borderRadius: '7px',
            textTransform: 'none',
            fontSize: '0.74rem',
            fontWeight: activeTab === 'projects' ? 700 : 500,
            color: activeTab === 'projects' ? 'var(--hp-primary)' : 'var(--hp-text-secondary)',
            bgcolor: activeTab === 'projects' ? 'var(--hp-surface-raised)' : 'transparent',
            whiteSpace: 'nowrap'
          }}
        >
          Projects
        </Button>
      </Box>

      {/* Active Project Selector Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          bgcolor: 'var(--hp-surface-raised)',
          borderRadius: '10px',
          border: '1px solid var(--hp-border)',
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Avatar
            sx={{
              bgcolor: `${selectedProject.color}25`,
              color: selectedProject.color,
              width: 28,
              height: 28,
              fontSize: '0.74rem',
              fontWeight: 800
            }}
          >
            {selectedProject.code}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--hp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selectedProject.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', fontSize: '0.68rem', display: 'block' }}>
              Key: {selectedProject.code}
            </Typography>
          </Box>
        </Box>

        {/* Project Selector Dropdown */}
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={selectedProject.id}
            onChange={(e) => {
              const target = projects.find((p) => p.id === e.target.value);
              if (target) onSelectProject(target);
            }}
            sx={{
              fontSize: '0.72rem',
              height: 26,
              '& .MuiSelect-select': { py: 0.2, px: 1 }
            }}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id} sx={{ fontSize: '0.8rem' }}>
                {p.code} - {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ======================================================== */}
      {/* TAB 1: ACTIVE SPRINT & SPRINT ISSUES */}
      {/* ======================================================== */}
      {activeTab === 'sprint' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 1.2 }}>
          {isLoadingSprint ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.5 }}>
              <CircularProgress size={28} sx={{ color: 'var(--hp-primary)' }} />
              <Typography variant="caption" color="var(--hp-text-secondary)">
                Calling /jira/projects/{selectedProject.code}/active-sprint...
              </Typography>
            </Box>
          ) : sprintError ? (
            <Alert
              severity="info"
              action={
                <Button size="small" color="inherit" onClick={() => loadSprintData(selectedProject.code)} sx={{ fontWeight: 700 }}>
                  Retry
                </Button>
              }
              sx={{ fontSize: '0.78rem', borderRadius: '10px' }}
            >
              {sprintError}
            </Alert>
          ) : !activeSprint ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'var(--hp-text-secondary)' }}>
              <Schedule sx={{ fontSize: 32, opacity: 0.4, mb: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                No Active Sprint
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', maxWidth: 240, mx: 'auto', mt: 0.5 }}>
                Project {selectedProject.code} has no running sprint. Start a sprint in Jira to explore real-time tasks.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Active Sprint Banner */}
              <Box
                sx={{
                  p: 1.2,
                  bgcolor: 'var(--hp-surface-raised)',
                  borderRadius: '10px',
                  border: '1px solid var(--hp-border)',
                  flexShrink: 0
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', color: 'var(--hp-text)' }}>
                    {activeSprint.name}
                  </Typography>
                  <Chip
                    label={activeSprint.state || 'ACTIVE'}
                    size="small"
                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(34, 201, 154, 0.14)', color: '#1ca87f' }}
                  />
                </Box>
                {activeSprint.goal && (
                  <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', display: 'block', mt: 0.4 }}>
                    Goal: {activeSprint.goal}
                  </Typography>
                )}
              </Box>

              {/* Sprint Issues Header & Filter */}
              <Box className="neumorphic-input" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, p: 0.6, flexShrink: 0 }}>
                <Search sx={{ color: 'var(--hp-text-secondary)', fontSize: 16 }} />
                <InputBase
                  placeholder="Filter sprint issues..."
                  value={storySearch}
                  onChange={(e) => setStorySearch(e.target.value)}
                  sx={{ flex: 1, fontSize: '0.82rem', color: 'var(--hp-text)' }}
                />
              </Box>

              {/* Status Filter Chips */}
              <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', flexShrink: 0, pb: 0.2 }}>
                {(['ALL', 'IN PROGRESS', 'DONE', 'TO DO'] as const).map((status) => (
                  <Chip
                    key={status}
                    label={status === 'ALL' ? 'All' : status}
                    size="small"
                    onClick={() => setStatusFilter(status)}
                    sx={{
                      height: 20,
                      fontSize: '0.64rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: statusFilter === status ? 'var(--hp-primary)' : 'var(--hp-surface-raised)',
                      color: statusFilter === status ? '#fff' : 'var(--hp-text-secondary)'
                    }}
                  />
                ))}
              </Box>

              {/* Sprint Issues List */}
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pr: 0.5 }}>
                {filteredIssues.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3, color: 'var(--hp-text-secondary)' }}>
                    <Typography variant="caption">No sprint issues found</Typography>
                  </Box>
                ) : (
                  filteredIssues.map((issue) => {
                    const statusStyle = getStatusColor(issue.status);
                    return (
                      <Box
                        key={issue.key}
                        onClick={() => setSelectedIssueKey(issue.key)}
                        className="neumorphic-card"
                        sx={{
                          p: 1.2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.6,
                          cursor: 'pointer',
                          borderRadius: '10px',
                          border: '1px solid var(--hp-border)',
                          '&:hover': { borderColor: 'var(--hp-primary)', bgcolor: 'var(--hp-surface-raised)' }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            {getIssueTypeIcon(issue.issueType)}
                            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--hp-primary)' }}>
                              {issue.key}
                            </Typography>
                          </Box>
                          <Chip
                            label={issue.status || 'To Do'}
                            size="small"
                            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, ...statusStyle }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: 'var(--hp-text)',
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {issue.summary}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.2 }}>
                          <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', fontSize: '0.68rem' }}>
                            {issue.assignee?.displayName || 'Unassigned'}
                          </Typography>
                          {issue.priority && (
                            <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', fontSize: '0.66rem', fontWeight: 600 }}>
                              {issue.priority}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ======================================================== */}
      {/* TAB 2: MY ACTIVE WORK (Filtered from active sprint state) */}
      {/* ======================================================== */}
      {activeTab === 'my-work' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 1.2 }}>
          <Box className="neumorphic-input" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, p: 0.6, flexShrink: 0 }}>
            <Search sx={{ color: 'var(--hp-text-secondary)', fontSize: 16 }} />
            <InputBase
              placeholder="Search your assigned issues..."
              value={storySearch}
              onChange={(e) => setStorySearch(e.target.value)}
              sx={{ flex: 1, fontSize: '0.82rem', color: 'var(--hp-text)' }}
            />
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pr: 0.5 }}>
            {myIssues.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'var(--hp-text-secondary)' }}>
                <PersonOutlined sx={{ fontSize: 32, opacity: 0.4, mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>No issues assigned to you</Typography>
                <Typography variant="caption">
                  No active sprint issues assigned to {user?.email || firebaseUser?.email} in {selectedProject.code}
                </Typography>
              </Box>
            ) : (
              filteredIssues.map((issue) => {
                const statusStyle = getStatusColor(issue.status);
                return (
                  <Box
                    key={issue.key}
                    onClick={() => setSelectedIssueKey(issue.key)}
                    className="neumorphic-card"
                    sx={{
                      p: 1.2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.6,
                      cursor: 'pointer',
                      borderRadius: '10px',
                      border: '1px solid var(--hp-border)',
                      '&:hover': { borderColor: 'var(--hp-primary)', bgcolor: 'var(--hp-surface-raised)' }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        {getIssueTypeIcon(issue.issueType)}
                        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--hp-primary)' }}>
                          {issue.key}
                        </Typography>
                      </Box>
                      <Chip
                        label={issue.status || 'To Do'}
                        size="small"
                        sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, ...statusStyle }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: 'var(--hp-text)',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {issue.summary}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.2 }}>
                      <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', fontSize: '0.68rem' }}>
                        {issue.assignee?.displayName || 'Unassigned'}
                      </Typography>
                      {issue.priority && (
                        <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', fontSize: '0.66rem', fontWeight: 600 }}>
                          {issue.priority}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      )}

      {/* ======================================================== */}
      {/* TAB 3: ALL PROJECTS */}
      {/* ======================================================== */}
      {activeTab === 'projects' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 1.2 }}>
          <Box className="neumorphic-input" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Search sx={{ color: 'var(--hp-text-secondary)', fontSize: 18 }} />
            <InputBase
              placeholder="Search projects..."
              value={projectSearch}
              onChange={(e) => setProjectSearch(e.target.value)}
              sx={{ flex: 1, fontSize: '0.86rem', color: 'var(--hp-text)' }}
            />
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pr: 0.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.2,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': { bgcolor: 'var(--hp-surface-raised)' }
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'var(--hp-surface)',
                  color: 'var(--hp-text)',
                  width: 36,
                  height: 36,
                  boxShadow: 'inset 2px 2px 5px #E0E7F0, inset -2px -2px 5px #FFFFFF'
                }}
              >
                <ViewModule fontSize="small" />
              </Avatar>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--hp-text)' }}>
                  All Projects
                </Typography>
                <Typography variant="caption" color="var(--hp-text-secondary)">
                  {projects.length} workspace projects
                </Typography>
              </Box>
            </Box>

            {filteredProjects.map((project) => {
              const isSelected = project.id === selectedProject.id;
              return (
                <Box
                  key={project.id}
                  onClick={() => {
                    onSelectProject(project);
                    setActiveTab('sprint');
                    if (isMobile && onOpenInChat) {
                      onOpenInChat();
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    p: 1.2,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'var(--hp-primary-light)' : 'transparent',
                    border: isSelected ? '1px solid rgba(79, 107, 255, 0.2)' : '1px solid transparent',
                    transition: 'all 0.18s ease',
                    '&:hover': { bgcolor: isSelected ? 'var(--hp-primary-light)' : 'var(--hp-surface-raised)' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: `${project.color}20`,
                        color: project.color,
                        width: 36,
                        height: 36,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}
                    >
                      {project.code}
                    </Avatar>
                    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                      <Typography
                        sx={{
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: '0.9rem',
                          color: isSelected ? 'var(--hp-primary)' : 'var(--hp-text)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {project.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="var(--hp-text-secondary)"
                        sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {project.epicCount} epics • {project.storyCount} stories
                      </Typography>
                    </Box>
                  </Box>

                  {isSelected && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      {isMobile && onOpenInChat ? (
                        <Button
                          size="small"
                          startIcon={<Chat sx={{ fontSize: 14 }} />}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            py: 0.2,
                            px: 1,
                            minWidth: 'auto',
                            bgcolor: 'var(--hp-primary)',
                            color: 'white',
                            borderRadius: '8px',
                            '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
                          }}
                        >
                          Chat
                        </Button>
                      ) : (
                        <MoreVert sx={{ color: 'var(--hp-primary)', fontSize: 18 }} />
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Jira Issue Details & Comments Modal Dialog */}
      <JiraIssueDetailsDialog
        open={Boolean(selectedIssueKey)}
        issueKey={selectedIssueKey}
        onClose={() => setSelectedIssueKey(null)}
      />
    </Box>
  );
};

export default ProjectsSidebar;
